import axios from "axios";

const parseFilename = (contentDisposition?: string, fallback = "download") => {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

const parseTagValue = (text: string | undefined, tag: string) => {
  const raw = String(text || "");
  const regex = new RegExp(`${tag}:([^\\s\\]]+)`, "i");
  const hit = raw.match(regex);
  return hit?.[1]?.trim() || "";
};

export const feesApi = {
  paymentModes: (params?: any) => axios.get("/system-settings/payment-mode", { params }).then((r) => r.data),
  students: async (params?: any) => {
    try {
      const res = await axios.get("/fees/students", { params });
      return res.data;
    } catch (error: any) {
      // Backward-compatible fallback for environments where /fees/students is not deployed yet.
      if (error?.response?.status !== 404) throw error;

      const legacy = await axios.get("/scholar-register/certificates/students", { params });
      const rows = Array.isArray(legacy.data) ? legacy.data : [];

      return rows.map((s: any) => ({
        studentId: s.id,
        studentSessionRecordId: `legacy-${s.id}-${params?.sessionId || "na"}`,
        firstName: s.firstName,
        lastName: s.lastName,
        scholarNumber: s.scholarNumber,
        admissionNumber: s.admissionNumber,
        class: s.class,
        section: s.section,
        session: params?.sessionId ? { id: params.sessionId, name: "Selected Session" } : undefined,
      }));
    }
  },
  deposit: (payload: any) => axios.post("/fees/deposit", payload).then((r) => r.data),
  listDeposits: (params?: any) => axios.get("/fees/deposit", { params }).then((r) => r.data),
  getDemand: (studentId: string, sessionId?: string, asOfDate?: string) =>
    axios.get(`/fees/demand/${studentId}`, { params: { sessionId, asOfDate } }).then((r) => r.data),
  getSummary: (studentId: string, sessionId?: string) =>
    axios.get(`/fees/summary/${studentId}`, { params: { sessionId } }).then((r) => r.data),
  bulkDeposit: (payload: any) => axios.post("/fees/deposit/bulk", payload).then((r) => r.data),
  cheques: async (params?: any) => {
    try {
      const res = await axios.get("/fees/cheques", { params });
      return res.data;
    } catch (error: any) {
      if (error?.response?.status !== 404) throw error;

      // Backward-compatible fallback for older backends where /fees/cheques is not deployed.
      const [depositsRes, receiptsRes] = await Promise.all([
        axios.get("/fees/deposit", {
          params: {
            // Do not pass sessionId to legacy deposits endpoint.
            // Older backend filters deposits by tx.remarks session tag,
            // but feeTransaction remarks may not carry the session tag.
            classId: params?.classId,
            sectionId: params?.sectionId,
            paymentMode: "CHEQUE",
            fromDate: params?.fromDate,
            toDate: params?.toDate,
          },
        }),
        axios
          .get("/fees/receipts", {
            params: {
              sessionId: params?.sessionId,
              classId: params?.classId,
              sectionId: params?.sectionId,
              fromDate: params?.fromDate,
              toDate: params?.toDate,
            },
          })
          .catch(() => ({ data: [] })),
      ]);

      const deposits = Array.isArray(depositsRes.data) ? depositsRes.data : [];
      const receipts = Array.isArray((receiptsRes as any).data) ? (receiptsRes as any).data : [];

      const receiptsByStudentAndCheque = new Map<string, any>();
      for (const r of receipts) {
        const chequeNo = parseTagValue(r?.remarks, "Cheque");
        if (!chequeNo || !r?.studentId) continue;
        receiptsByStudentAndCheque.set(`${r.studentId}::${chequeNo}`, r);
      }

      const mapped = deposits
        .filter((d: any) => String(d?.paymentMethod || "").toUpperCase() === "CHEQUE")
        .map((d: any) => {
          const chequeNo = d?.chequeNumber || parseTagValue(d?.remarks, "Cheque");
          const bankName = parseTagValue(d?.remarks, "Bank");
          const chequeDate = parseTagValue(d?.remarks, "ChequeDate");
          const linkedReceipt = receiptsByStudentAndCheque.get(`${d?.studentId}::${chequeNo}`);

          return {
            id: d.id,
            studentId: d.studentId,
            studentName: `${d?.student?.firstName || ""} ${d?.student?.lastName || ""}`.trim(),
            scholarNo: d?.student?.scholarNumber || "-",
            classSection: `${d?.student?.class?.name || "-"} - ${d?.student?.section?.name || "-"}`,
            chequeNo: chequeNo || "-",
            bankName: bankName || "-",
            amount: Number(d?.amount || 0),
            chequeDate: chequeDate || d?.paymentDate,
            status: d?.status || "PENDING",
            receiptId: linkedReceipt?.id || null,
            receiptNo: linkedReceipt?.receiptNo || null,
            receiptStatus: linkedReceipt?.status || null,
          };
        });

      let filtered = mapped;

      if (params?.sessionId) {
        // Session-scoped filtering in legacy mode is derived from receipts,
        // because receipts consistently store the [SESSION:...] tag.
        filtered = filtered.filter((r: any) => Boolean(r.receiptId));
      }

      if (params?.status) {
        filtered = filtered.filter((r: any) => r.status === params.status);
      }

      return filtered;
    }
  },
  reconcileCheque: (id: string, payload: any) => axios.patch(`/fees/cheque/${id}/reconcile`, payload).then((r) => r.data),
  receipts: (params?: any) => axios.get("/fees/receipts", { params }).then((r) => r.data),
  receiptById: (receiptId: string) => axios.get(`/fees/receipt/${receiptId}`).then((r) => r.data),
  getReceiptPdfBlob: async (receiptId: string) => {
    const res = await axios.get(`/fees/receipt/${receiptId}/pdf`, { responseType: "blob" });
    return {
      blob: res.data,
      filename: parseFilename(res.headers?.["content-disposition"], `${receiptId}.pdf`),
    };
  },
  getReceiptsZipBlob: async (receiptIds: string[]) => {
    const res = await axios.post(
      "/fees/receipts/zip",
      { receiptIds },
      { responseType: "blob" },
    );
    return {
      blob: res.data,
      filename: parseFilename(res.headers?.["content-disposition"], "fee-receipts.zip"),
    };
  },
  reprintReceipt: (receiptId: string) => axios.post(`/fees/receipt/${receiptId}/reprint`).then((r) => r.data),
  cancelReceipt: async (id: string, reason?: string) => {
    try {
      const res = await axios.patch(`/fees/receipt/${id}/cancel`, { reason });
      return res.data;
    } catch (error: any) {
      if (error?.response?.status !== 404) throw error;
      const res = await axios.post(`/fees/receipt/${id}/cancel`, { reason });
      return res.data;
    }
  },
  discount: (payload: any) => axios.post("/fees/discount", payload).then((r) => r.data),
  discountClass: (payload: any) => axios.post("/fees/discount/class", payload).then((r) => r.data),
  discounts: (params?: any) => axios.get("/fees/discount", { params }).then((r) => r.data),
  reverseDiscount: (id: string, payload?: any) => axios.patch(`/fees/discount/${id}/reverse`, payload || {}).then((r) => r.data),
  deleteDiscount: (id: string) => axios.delete(`/fees/discount/${id}`).then((r) => r.data),
  fine: (payload: any) => axios.post("/fees/fine", payload).then((r) => r.data),
  deleteFine: (id: string) => axios.delete(`/fees/fine/${id}`).then((r) => r.data),
  refundableReceipts: (params?: any) => axios.get("/fees/refund/receipts", { params }).then((r) => r.data),
  refund: (payload: any) => axios.post("/fees/refund", payload).then((r) => r.data),
  refundHistory: (params?: any) => axios.get("/fees/refund/history", { params }).then((r) => r.data),
  getRefundPdfBlob: async (refundId: string) => {
    const res = await axios.get(`/fees/refund/${refundId}/pdf`, { responseType: "blob" });
    return {
      blob: res.data,
      filename: parseFilename(res.headers?.["content-disposition"], `${refundId}.pdf`),
    };
  },
  studentLedger: async (studentId: string, params?: any) => {
    try {
      const res = await axios.get(`/fees/ledger/student/${studentId}`, { params });
      return res.data;
    } catch (error: any) {
      // Backward-compatible fallback for environments where the new ledger path is not deployed yet.
      if (error?.response?.status !== 404) throw error;
      const legacy = await axios.get(`/fees/ledger/${studentId}`, { params: { sessionId: params?.sessionId } });
      return legacy.data;
    }
  },
  classLedger: (params?: any) => axios.get("/fees/ledger/class", { params }).then((r) => r.data),
  ledgerDaybook: (params?: any) => axios.get("/fees/ledger/daybook", { params }).then((r) => r.data),
  feeAudit: (params?: any) => axios.get("/fees/audit", { params }).then((r) => r.data),
  getLedgerExportBlob: async (params: any) => {
    const res = await axios.get("/fees/ledger/export", { params, responseType: "blob" });
    const ext = String(params?.format || "xlsx").toLowerCase();
    return {
      blob: res.data,
      filename: parseFilename(res.headers?.["content-disposition"], `fee-ledger-export.${ext}`),
    };
  },
  ledger: (studentId: string, sessionId?: string) => axios.get(`/fees/ledger/${studentId}`, { params: { sessionId } }).then((r) => r.data),
  noDuesCheck: (studentId: string, sessionId?: string) =>
    axios.get(`/fees/no-dues/check/${studentId}`, { params: { sessionId } }).then((r) => r.data),
  generateNoDuesCertificateBlob: async (payload: { studentId: string; sessionId: string }) => {
    const res = await axios.post("/fees/no-dues/generate", payload, { responseType: "blob" });
    return {
      blob: res.data,
      filename: parseFilename(res.headers?.["content-disposition"], `no-dues-${payload.studentId}.pdf`),
    };
  },
  daybook: (params?: any) => axios.get("/fees/daybook", { params }).then((r) => r.data),
  getDaybookExportBlob: async (params: { date: string; sessionId?: string; format: "csv" | "xlsx" }) => {
    const res = await axios.get("/fees/daybook/export", { params, responseType: "blob" });
    return {
      blob: res.data,
      filename: parseFilename(res.headers?.["content-disposition"], `daybook-${params.date}.${params.format}`),
    };
  },
};
